import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowUpRight } from "lucide-react";
import { POSTS, CATEGORIES } from "@/lib/blog";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const Blog = () => {
  useDocumentMeta({
    title: "Blog — Antivirus Buying Guides, Reviews & Privacy Tips",
    description:
      "Expert antivirus buying guides, brand comparisons, privacy tips and threat updates from the ProsoftMarketplace team.",
  });

  const [category, setCategory] = useState("All");
  const filtered = useMemo(
    () => (category === "All" ? POSTS : POSTS.filter((p) => p.category === category)),
    [category],
  );
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="blog-page" className="bg-slate-50">
      {/* Header band */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Blog · {POSTS.length} articles
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            Cyber-safety, plainly explained.
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-600">
            Honest buying guides, brand comparisons and privacy tips from the ProsoftMarketplace team —
            written for normal people, not for IT departments.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              Category:
            </span>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${
                  category === c
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-300 text-slate-700 hover:border-slate-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured + Grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {featured && (
          <Link
            to={`/blog/${featured.slug}`}
            data-testid={`blog-featured-${featured.slug}`}
            className="group block border-2 border-slate-900 bg-white transition-all hover:shadow-[10px_10px_0_0_rgba(16,185,129,1)]"
          >
            <div className="grid lg:grid-cols-2">
              <div className="aspect-[16/10] overflow-hidden lg:aspect-auto">
                <img
                  src={featured.cover}
                  alt={featured.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col justify-center p-6 sm:p-10">
                <div className="inline-flex w-fit items-center bg-emerald-500 px-2 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white">
                  Featured · {featured.category}
                </div>
                <h2 className="mt-4 font-display text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-3 text-slate-600">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {formatDate(featured.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {featured.readMinutes} min read
                  </span>
                </div>
                <div className="mt-6 inline-flex items-center gap-1 font-bold uppercase tracking-[0.15em] text-slate-900 group-hover:text-emerald-600">
                  Read article <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </Link>
        )}

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group flex flex-col border border-slate-200 bg-white transition-all hover:border-slate-900 hover:shadow-[6px_6px_0_0_rgba(15,23,42,1)]"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
                  {p.category}
                </div>
                <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-slate-900">
                  {p.title}
                </h3>
                <p className="mt-2 line-clamp-3 text-sm text-slate-600">{p.excerpt}</p>
                <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {formatDate(p.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {p.readMinutes} min
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Blog;
